<template>
  <div class="max-w-6xl mx-auto">
    <!-- Page Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold mb-4">🏆 Competitions</h1>
      <p class="text-base-content/70 max-w-2xl mx-auto">
        Browse and join competitions to test your stock picking skills against other traders.
      </p>
    </div>

    <!-- Available Competitions Section -->
    <div class="card bg-base-100 card-bordered">
      <div class="card-body">
        <div class="flex justify-between items-center mb-6">
          <h2 class="card-title text-xl">
            <i class="mdi mdi-trophy text-primary"></i>
            Available Competitions
          </h2>
          <button 
            @click="refreshCompetitions"
            class="btn btn-ghost btn-sm"
            :disabled="loading"
          >
            <i class="mdi mdi-refresh"></i>
            Refresh
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <span class="loading loading-spinner loading-lg"></span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-error">
          <i class="mdi mdi-alert-circle h-6 w-6"></i>
          <span>{{ error }}</span>
        </div>

        <!-- No Competitions -->
        <div v-else-if="availableCompetitions.length === 0" class="text-center py-12">
          <div class="max-w-md mx-auto">
            <div class="text-6xl mb-4">🏆</div>
            <h4 class="text-lg font-semibold mb-2">No competitions available</h4>
            <p class="text-base-content/70 mb-6">
              There are no competitions open for joining at the moment.
            </p>
            <router-link to="/create" class="btn btn-primary">
              Create a Competition
            </router-link>
          </div>
        </div>

        <!-- Competitions List -->
        <div v-else class="space-y-6">
          <div 
            v-for="competition in availableCompetitions" 
            :key="competition.id" 
            class="relative"
          >
            <!-- Competition Header -->
            <CompetitionHeader 
              :competition="competition"
              :participant-count="competition.participantCount || 0"
              :show-delete-button="false"
              :loading="competition.loadingCount"
            />
            
            <!-- Competition Actions -->
            <div class="card bg-base-100 card-bordered mt-4">
              <div class="card-body p-4">
                <div class="flex justify-between items-center">
                  <div class="flex items-center gap-4">
                    <!-- Competition Stats -->
                    <div class="flex items-center gap-6 text-sm">
                      <div class="flex items-center gap-2">
                        <i class="mdi mdi-chart-line text-primary"></i>
                        <span>{{ competition.required_stock_count }} stocks</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <i class="mdi mdi-weight text-success"></i>
                        <span :class="competition.allow_weights ? 'text-success' : 'text-base-content/70'">
                          {{ competition.allow_weights ? 'Weighted' : 'Equal Weight' }}
                        </span>
                      </div>
                      <div class="flex items-center gap-2">
                        <i class="mdi mdi-account-group text-info"></i>
                        <span>{{ competition.privacy_mode }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Action Buttons -->
                  <div class="flex gap-2">
                    <router-link 
                      :to="`/competition/${competition.id}`" 
                      class="btn btn-ghost btn-sm"
                    >
                      <i class="mdi mdi-eye"></i>
                      View Details
                    </router-link>
                    <router-link 
                      :to="`/join-form?id=${competition.id}`" 
                      class="btn btn-primary btn-sm"
                    >
                      <i class="mdi mdi-plus"></i>
                      Join Competition
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useCompetitionStore } from '../stores/competition'
import CompetitionHeader from '../components/CompetitionHeader.vue'

const competitionStore = useCompetitionStore()

// Available competitions state
const loading = ref(false)
const error = ref('')
const availableCompetitions = ref([])

const fetchAvailableCompetitions = async () => {
  loading.value = true
  error.value = ''
  
  try {
    const competitions = await competitionStore.fetchCompetitions()
    // Filter to only show competitions that haven't started yet
    if (competitions && Array.isArray(competitions)) {
      const filteredCompetitions = competitions.filter(comp => !comp.is_locked)
      
      // Fetch participant counts for each competition
      const competitionsWithCounts = await Promise.all(
        filteredCompetitions.map(async (competition) => {
          try {
            const leaderboard = await competitionStore.fetchLeaderboard(competition.id)
            return {
              ...competition,
              participantCount: leaderboard?.entries?.length || 0,
              loadingCount: false
            }
          } catch (err) {
            console.warn(`Failed to fetch participant count for competition ${competition.id}:`, err)
            return {
              ...competition,
              participantCount: 0,
              loadingCount: false
            }
          }
        })
      )
      
      availableCompetitions.value = competitionsWithCounts
    } else {
      availableCompetitions.value = []
      console.warn('fetchCompetitions returned unexpected data:', competitions)
    }
  } catch (err) {
    error.value = err.message || 'Failed to load competitions'
    availableCompetitions.value = []
    
    // Fallback: try to use competitions from store state
    if (competitionStore.competitions && Array.isArray(competitionStore.competitions)) {
      availableCompetitions.value = competitionStore.competitions.filter(comp => !comp.is_locked)
      error.value = '' // Clear error if fallback works
    }
  } finally {
    loading.value = false
  }
}

const refreshCompetitions = () => {
  fetchAvailableCompetitions()
}

onMounted(() => {
  fetchAvailableCompetitions()
})
</script> 